/**
 * Render público dos blocos da página customizada do checkout (builder do
 * hub Waid Pay). Contrato: ICheckoutPage {blocks, aside}.
 *
 * - <PageBlocks/>: conteúdo acima do grid form+sidebar (suporta `columns`).
 * - <PageAsideBlocks/>: widgets da coluna lateral, abaixo do resumo.
 *
 * `orderBump` NÃO renderiza aqui — o checkout tem o sistema nativo de order
 * bump (OrderBumpContext); o bloco do builder é ignorado até a unificação.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
	FaBarcode,
	FaCcAmex,
	FaCcMastercard,
	FaCcVisa,
	FaCheckCircle,
	FaChevronDown,
	FaClock,
	FaLock,
	FaQuestionCircle,
	FaQuoteLeft,
	FaShieldAlt
} from 'react-icons/fa';
import { PixIcon } from '../../../../components/icons/PixIcon';
import { checkoutsApi } from '../../../../api/checkouts';
import { contentsApi } from '../../../../api/contents';
import { OrderBumpContext } from '../Context/OrderBumpContext';
import { Checkbox } from '../../../../components/Inputs/Checkbox';
import { formatCurrency } from '../../../../utils/formatCurrency';
import {
	ICheckoutBlock,
	ICheckoutPage,
	ICheckoutPageSettings
} from '../../../../api/checkouts/types/checkout-page.type';
import {
	usePreviewBridge,
	usePreviewLayoutReporter
} from './use-preview-bridge';
import { isPreviewMode } from './use-preview-bridge';
import { CheckoutPageSlotsContext } from '../Context/CheckoutPageContext';
import './styles.scss';
import './checkout-modern.scss';

/** Página customizada do checkout — silenciosa quando não existe/falha. */
export const useCheckoutPage = (
	checkoutId?: number
): ICheckoutPage | undefined => {
	const { data: fetched } = useQuery(
		[checkoutsApi.findPage.key, { checkoutId }],
		checkoutsApi.findPage.fetch,
		{
			enabled: !!checkoutId,
			retry: 1,
			staleTime: 60_000,
			// Página sem blocos/endpoint fora do ar → checkout padrão.
			onError: () => undefined
		}
	);

	// Modo preview do builder (?_preview=1): o rascunho do editor manda.
	const draft = usePreviewBridge();
	const data = draft ?? fetched;

	// Reporta a posição dos blocos para o editor desenhar os controles.
	usePreviewLayoutReporter(!!data);

	/**
	 * Dev: `?_layout=steps&_theme=dark&_address=hidden` sobrepõem as settings
	 * para testar o render local sem depender do backend deployado. Inerte em
	 * produção (build de produção ignora).
	 */
	const settings = useMemo(() => {
		const base = data?.settings;
		if (process.env.NODE_ENV === 'production') return base;

		const params = new URLSearchParams(window.location.search);
		const layout = params.get('_layout');
		const theme = params.get('_theme');
		const address = params.get('_address');
		if (!layout && !theme && !address) return base;

		return {
			...base,
			...(layout === 'steps' || layout === 'single'
				? { layout }
				: undefined),
			...(theme === 'dark' || theme === 'light' || theme === 'auto'
				? { theme }
				: undefined),
			...(address === 'hidden' || address === 'required'
				? { fields: { ...base?.fields, address } }
				: undefined)
		} as ICheckoutPageSettings;
	}, [data?.settings]);

	/**
	 * Tema por checkout: 'dark'/'light' mandam; 'auto' segue o aparelho do
	 * comprador. Sem configuração, nada é tocado — vale o tema da plataforma
	 * (customization.theme), aplicado no App.
	 */
	useEffect(() => {
		const theme = settings?.theme;
		if (!theme) return;

		const prefersDark =
			theme === 'dark' ||
			(theme === 'auto' &&
				window.matchMedia?.('(prefers-color-scheme: dark)').matches);

		// Marca a precedência antes de aplicar: o App checa esta flag para
		// não reimpor o tema da plataforma por cima.
		document.body.dataset.themeOverride = theme;
		document.body.classList.toggle('dark-mode', prefersDark);

		return () => {
			delete document.body.dataset.themeOverride;
		};
	}, [settings?.theme]);

	/** Cor de destaque do checkout (botões, abas, realces). */
	useEffect(() => {
		const accent = settings?.accentColor;
		const root = document.documentElement;
		if (!accent) return;

		const previous = root.style.getPropertyValue('--checkout-accent');
		const previousSoft = root.style.getPropertyValue(
			'--checkout-accent-soft'
		);
		root.style.setProperty('--checkout-accent', accent);
		// Versão suave para fundos/realces, derivada da mesma cor.
		root.style.setProperty('--checkout-accent-soft', `${accent}1A`);

		return () => {
			root.style.setProperty('--checkout-accent', previous);
			root.style.setProperty('--checkout-accent-soft', previousSoft);
		};
	}, [settings?.accentColor]);

	useSeoSettings(settings);

	return useMemo(
		() => (data ? { ...data, settings } : data),
		[data, settings]
	);
};


/**
 * SEO por página: título e descrição da aba/compartilhamento. SPA, então
 * vale para navegadores e crawlers que executam JS; restaurado ao sair.
 */
export const useSeoSettings = (settings?: ICheckoutPageSettings) => {
	useEffect(() => {
		const seo = settings?.seo;
		if (!seo?.title && !seo?.description) return;

		const previousTitle = document.title;
		if (seo.title) document.title = seo.title;

		let meta = document.querySelector<HTMLMetaElement>(
			'meta[name="description"]'
		);
		const previousDescription = meta?.getAttribute('content') ?? null;
		let created = false;
		if (seo.description) {
			if (!meta) {
				meta = document.createElement('meta');
				meta.setAttribute('name', 'description');
				document.head.appendChild(meta);
				created = true;
			}
			meta.setAttribute('content', seo.description);
		}

		return () => {
			document.title = previousTitle;
			if (created) meta?.remove();
			else if (meta && previousDescription !== null)
				meta.setAttribute('content', previousDescription);
		};
	}, [settings?.seo?.title, settings?.seo?.description]);
};

const pad = (value: number) => String(value).padStart(2, '0');

/**
 * Cronômetro de oferta. `endsAt` (ISO) fixa o fim pra todos; sem `endsAt`,
 * `durationMinutes` cria um deadline POR VISITANTE, persistido em
 * localStorage pra sobreviver ao refresh (padrão de escassez do mercado).
 */
const CountdownBlock = ({
	block,
	checkoutId
}: {
	block: ICheckoutBlock;
	checkoutId?: number;
}) => {
	const deadline = useMemo(() => {
		if (block.props.endsAt) {
			const fixed = new Date(block.props.endsAt).getTime();
			return Number.isNaN(fixed) ? null : fixed;
		}
		const minutes = Number(block.props.durationMinutes) || 15;
		const storageKey = `checkout_countdown:${checkoutId ?? 'x'}:${
			block.id
		}`;
		try {
			const stored = window.localStorage.getItem(storageKey);
			if (stored) return parseInt(stored, 10);
			const fresh = Date.now() + minutes * 60_000;
			window.localStorage.setItem(storageKey, String(fresh));
			return fresh;
		} catch {
			return Date.now() + minutes * 60_000;
		}
	}, [block.id, block.props.endsAt, block.props.durationMinutes, checkoutId]);

	const [remaining, setRemaining] = useState(() =>
		deadline ? Math.max(0, deadline - Date.now()) : 0
	);

	useEffect(() => {
		if (!deadline) return;
		const interval = window.setInterval(() => {
			setRemaining(Math.max(0, deadline - Date.now()));
		}, 1000);
		return () => window.clearInterval(interval);
	}, [deadline]);

	if (!deadline) return null;

	const totalSeconds = Math.floor(remaining / 1000);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const variant = block.props.variant || 'bar';

	// 'boxes': dígitos em caixas separadas (clássico de lançamento).
	if (variant === 'boxes') {
		const units: [number, string][] = [
			...(hours > 0 ? ([[hours, 'horas']] as [number, string][]) : []),
			[minutes, 'min'],
			[seconds, 'seg']
		];
		return (
			<div className="page-block page-block-countdown-boxes" role="timer">
				<span className="page-block-countdown-text">
					{block.props.text || 'Oferta por tempo limitado'}
				</span>
				<div>
					{units.map(([value, label]) => (
						<span
							key={label}
							className="page-block-countdown-box"
							style={{
								background: block.props.bgColor || undefined,
								color: block.props.textColor || undefined
							}}
						>
							<b>{pad(value)}</b>
							<small>{label}</small>
						</span>
					))}
				</div>
			</div>
		);
	}

	// 'subtle': linha discreta, sem caixa.
	if (variant === 'subtle') {
		return (
			<div
				className="page-block page-block-countdown-subtle"
				role="timer"
			>
				<FaClock aria-hidden />
				<span>
					{block.props.text || 'Oferta por tempo limitado'}{' '}
					<b>
						{hours > 0 && `${pad(hours)}:`}
						{pad(minutes)}:{pad(seconds)}
					</b>
				</span>
			</div>
		);
	}

	return (
		<div
			className="page-block page-block-countdown"
			role="timer"
			style={{
				background: block.props.bgColor || undefined,
				color: block.props.textColor || undefined
			}}
		>
			<FaClock aria-hidden />
			<span className="page-block-countdown-text">
				{block.props.text || 'Oferta por tempo limitado'}
			</span>
			<span className="page-block-countdown-clock">
				{hours > 0 && `${pad(hours)}:`}
				{pad(minutes)}:{pad(seconds)}
			</span>
		</div>
	);
};

/** YouTube/Vimeo → URL de embed (nocookie no YouTube). */
const toEmbedUrl = (url: string): string | null => {
	try {
		const parsed = new URL(url);
		const host = parsed.hostname.replace(/^www\./, '');
		if (host === 'youtu.be') {
			return `https://www.youtube-nocookie.com/embed/${parsed.pathname.slice(
				1
			)}`;
		}
		if (host === 'youtube.com' || host === 'm.youtube.com') {
			const id = parsed.searchParams.get('v');
			if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
			if (parsed.pathname.startsWith('/embed/')) return url;
		}
		if (host === 'vimeo.com') {
			const id = parsed.pathname.split('/').filter(Boolean)[0];
			if (id) return `https://player.vimeo.com/video/${id}`;
		}
		if (host === 'player.vimeo.com') return url;
		return null;
	} catch {
		return null;
	}
};

/**
 * Prova social "comprou há X minutos": toast fixo no canto que alterna os
 * nomes configurados. Os minutos são sorteados por exibição (2–45) — é uma
 * peça de marketing configurada pelo admin, como nos checkouts do mercado.
 * No modo de edição fica sempre visível para poder ser selecionado.
 */
const SalesNotificationBlock = ({ block }: { block: ICheckoutBlock }) => {
	const names: string[] = String(block.props.names || '')
		.split('\n')
		.map((name) => name.trim())
		.filter(Boolean);

	const [index, setIndex] = useState(0);
	// 'shown' anima subindo; 'leaving' anima descendo antes de sumir.
	const [phase, setPhase] = useState<'hidden' | 'shown' | 'leaving'>(
		isPreviewMode() ? 'shown' : 'hidden'
	);
	const [minutes, setMinutes] = useState(12);

	useEffect(() => {
		if (isPreviewMode() || !names.length) return;

		const showFor = 6_000;
		const every = Math.max(8, Number(block.props.intervalSeconds) || 15);

		let leaveTimer = 0;
		let hideTimer = 0;
		const showTimer = window.setInterval(() => {
			setIndex((prev) => (prev + 1) % names.length);
			setMinutes(2 + Math.floor(Math.random() * 44));
			setPhase('shown');
			leaveTimer = window.setTimeout(() => setPhase('leaving'), showFor);
			hideTimer = window.setTimeout(
				() => setPhase('hidden'),
				showFor + 400
			);
		}, every * 1000);

		return () => {
			window.clearInterval(showTimer);
			window.clearTimeout(leaveTimer);
			window.clearTimeout(hideTimer);
		};
	}, [names.length, block.props.intervalSeconds]);

	if (!names.length || phase === 'hidden') return null;

	return (
		<div
			className={`page-block-sales-note ${
				phase === 'leaving' ? 'page-block-sales-note--leaving' : ''
			}`}
			role="status"
		>
			<FaCheckCircle aria-hidden />
			<div>
				<p>
					<b>{names[index % names.length]}</b>{' '}
					{block.props.action || 'acabou de comprar'}
					{block.props.product ? ` ${block.props.product}` : ''}
				</p>
				<span>há {minutes} minutos</span>
			</div>
		</div>
	);
};

/**
 * Order bump do builder LIGADO à mecânica real: com `offerUuid` (a mesma
 * oferta do order bump nativo), busca o conteúdo e liga o checkbox ao
 * OrderBumpContext — o item entra de verdade no pagamento. Vários blocos
 * podem existir; a seleção é exclusiva porque o pagamento aceita UM bump.
 * Sem offerUuid, vira um destaque visual (sem checkbox).
 */
const OrderBumpBlock = ({ block }: { block: ICheckoutBlock }) => {
	const { props } = block;
	const { orderBumps, toggleOrderBump } = useContext(OrderBumpContext);
	const offerUuid: string = props.offerUuid || '';

	// O seletor do builder manda o uuid do CONTEÚDO (contents/uuid). O
	// endpoint /order-bump/:uuid fica de fallback para uuids legados de
	// order bump gerados no admin antigo.
	const { data: byUuid } = useQuery(
		[contentsApi.getByUuid.key, { uuid: offerUuid }],
		contentsApi.getByUuid.fetch,
		{ enabled: !!offerUuid, refetchOnWindowFocus: false, retry: false }
	);
	const { data: legacy } = useQuery(
		[contentsApi.getOrderBump.key, { uuid: offerUuid }],
		contentsApi.getOrderBump.fetch,
		{
			enabled: !!offerUuid && byUuid === null,
			refetchOnWindowFocus: false,
			retry: false
		}
	);
	const offer = byUuid ?? legacy;

	const isChecked = !!offer && orderBumps.some((b) => b.uuid === offerUuid);
	const variant = props.variant || 'card';

	const image = offer?.images?.[0];
	const imageUrl = image ? `${image.path}${image.name}` : null;

	return (
		<label
			className={`page-block page-block-orderbump page-block-orderbump--${variant} ${
				isChecked ? 'page-block-orderbump--checked' : ''
			}`}
		>
			{offer ? (
				<Checkbox
					label=""
					wrapperClassName="checkbox-lg"
					checked={isChecked}
					onChange={(e: React.FormEvent<HTMLInputElement>) =>
						toggleOrderBump(
							{ uuid: offerUuid, content: offer },
							e.currentTarget.checked
						)
					}
				/>
			) : (
				<span className="page-block-orderbump-icon" aria-hidden>
					🎁
				</span>
			)}
			{variant !== 'minimal' && imageUrl && (
				<img
					className="page-block-orderbump-thumb"
					src={imageUrl}
					alt=""
				/>
			)}
			<div className="page-block-orderbump-body">
				<p>{props.title || offer?.title || 'Leve também'}</p>
				{props.description && <span>{props.description}</span>}
				{props.note && <small>{props.note}</small>}
			</div>
			{offer && (
				<b className="page-block-orderbump-price">
					{formatCurrency(offer.pricing.price)}
				</b>
			)}
		</label>
	);
};


type PricingColumn = {
	title?: string;
	price?: string;
	cycle?: string;
	priceNote?: string;
	priceYearly?: string;
	cycleYearly?: string;
	priceNoteYearly?: string;
	ctaUrlYearly?: string;
	description?: string;
	features?: string;
	ctaLabel?: string;
	ctaUrl?: string;
	highlight?: boolean;
	highlightLabel?: string;
};

/** Valores exibidos conforme o período do alternador mensal/anual. */
const columnForPeriod = (column: PricingColumn, yearly: boolean) =>
	yearly
		? {
				price: column.priceYearly || column.price,
				cycle: column.cycleYearly ?? 'ano',
				priceNote: column.priceNoteYearly || column.priceNote,
				ctaUrl: column.ctaUrlYearly || column.ctaUrl
		  }
		: {
				price: column.price,
				cycle: column.cycle,
				priceNote: column.priceNote,
				ctaUrl: column.ctaUrl
		  };

const featureLines = (column: PricingColumn): string[] =>
	String(column.features || '')
		.split('\n')
		.map((feature) => feature.trim())
		.filter(Boolean);

/**
 * Tabela de planos em 3 apresentações:
 * - cards: colunas destacadas, com "Ver mais" quando a lista de funções
 *   passa de `collapseAfter` (resumo + expansão);
 * - minimal: colunas limpas, sem caixa;
 * - comparison: TABELA COMPARATIVA — as linhas são a união das funções de
 *   todos os planos (na ordem de primeira aparição) e cada plano ganha ✓
 *   nas que possui. Sem editor extra: deriva das mesmas listas dos cards.
 */
const PricingTableBlock = ({ block }: { block: ICheckoutBlock }) => {
	const { props } = block;
	const columns: PricingColumn[] = props.columns ?? [];
	const collapseAfter = Number(props.collapseAfter) || 0;
	const [expanded, setExpanded] = useState<Record<number, boolean>>({});
	// Alternador mensal/anual (períodos por coluna via priceYearly etc.).
	const [yearly, setYearly] = useState(false);

	if (!columns.length) return null;

	const variant = props.variant || 'cards';

	const billingToggle = !!props.billingToggle && (
		<div className="pricing-toggle" role="tablist">
			<button
				type="button"
				className={!yearly ? 'is-active' : ''}
				onClick={() => setYearly(false)}
			>
				{props.monthlyLabel || 'Mensal'}
			</button>
			<button
				type="button"
				className={yearly ? 'is-active' : ''}
				onClick={() => setYearly(true)}
			>
				{props.yearlyLabel || 'Anual'}
				{props.savingsNote && (
					<em
						style={{
							background: props.accentColor || undefined
						}}
					>
						{props.savingsNote}
					</em>
				)}
			</button>
		</div>
	);

	if (variant === 'comparison') {
		// Linhas '## Nome' viram cabeçalhos de seção na tabela.
		const allFeatures: string[] = [];
		columns.forEach((column) =>
			featureLines(column).forEach((feature) => {
				if (!allFeatures.includes(feature)) allFeatures.push(feature);
			})
		);
		return (
			<div
				className="page-block page-block-pricing-comparison"
				style={
					props.bgColor
						? {
								background: props.bgColor,
								padding: '20px',
								borderRadius: 20
						  }
						: undefined
				}
			>
				<table>
					<thead>
						<tr>
							<th aria-label="Funções" />
							{columns.map((column, index) => (
								<th
									key={index}
									className={
										column.highlight
											? 'is-highlight'
											: undefined
									}
								>
									<span className="comparison-plan">
										{column.title}
									</span>
									<span className="comparison-price">
										{columnForPeriod(column, yearly)
											.price}
										{columnForPeriod(column, yearly)
											.cycle && (
											<small>
												/
												{
													columnForPeriod(
														column,
														yearly
													).cycle
												}
											</small>
										)}
									</span>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{allFeatures.map((feature) =>
							feature.startsWith('## ') ? (
								<tr
									key={feature}
									className="comparison-section"
								>
									<td colSpan={columns.length + 1}>
										{feature.slice(3)}
									</td>
								</tr>
							) : (
							<tr key={feature}>
								<td>{feature}</td>
								{columns.map((column, index) => (
									<td
										key={index}
										className={
											column.highlight
												? 'is-highlight'
												: undefined
										}
									>
										{featureLines(column).includes(
											feature
										) ? (
											<FaCheckCircle
												aria-label="Incluído"
												style={{
													color:
														props.accentColor ||
														'#10b981'
												}}
											/>
										) : (
											<span className="comparison-no">
												—
											</span>
										)}
									</td>
								))}
							</tr>
							)
						)}
						<tr className="comparison-cta-row">
							<td />
							{columns.map((column, index) => (
								<td
									key={index}
									className={
										column.highlight
											? 'is-highlight'
											: undefined
									}
								>
									{column.ctaUrl && (
										<a
											className="pricing-cta"
											href={column.ctaUrl}
											style={
												column.highlight
													? {
															background:
																props.accentColor ||
																undefined,
															color: '#fff'
													  }
													: undefined
											}
										>
											{column.ctaLabel || 'Assinar'}
										</a>
									)}
								</td>
							))}
						</tr>
					</tbody>
				</table>
			</div>
		);
	}

	const cardStyle = props.cardStyle || 'outline';
	return (
		<div
			className="page-block page-block-pricing-wrap"
			style={
				props.bgColor
					? {
							background: props.bgColor,
							padding: '28px 24px',
							borderRadius: 20
					  }
					: undefined
			}
		>
			{billingToggle}
		<div
			className={`page-block-pricing ${
				variant === 'minimal' ? 'page-block-pricing--minimal' : ''
			} ${
				variant === 'horizontal'
					? 'page-block-pricing--horizontal'
					: ''
			} page-block-pricing--style-${cardStyle}`}
		>
			{columns.map((column, index) => {
				const period = columnForPeriod(column, yearly);
				const features = featureLines(column).filter(
					(feature) => !feature.startsWith('## ')
				);
				const isExpanded = !!expanded[index];
				const visible =
					collapseAfter > 0 && !isExpanded
						? features.slice(0, collapseAfter)
						: features;
				const hasMore =
					collapseAfter > 0 && features.length > collapseAfter;
				return (
					<div
						key={index}
						className={`pricing-column ${
							column.highlight ? 'pricing-column--highlight' : ''
						}`}
						style={
							column.highlight && props.accentColor
								? { borderColor: props.accentColor }
								: undefined
						}
					>
						{column.highlight && (
							<span
								className="pricing-badge"
								style={{
									background: props.accentColor || undefined
								}}
							>
								{column.highlightLabel || 'Recomendado'}
							</span>
						)}
						<p className="pricing-title">{column.title}</p>
						{column.description && (
							<p className="pricing-description">
								{column.description}
							</p>
						)}
						<p className="pricing-price">
							{period.price}
							{period.cycle && <small>/{period.cycle}</small>}
						</p>
						{period.priceNote && (
							<p className="pricing-price-note">
								{period.priceNote}
							</p>
						)}
						<ul className="pricing-features">
							{visible.map((feature, i) => (
								<li key={i}>
									<FaCheckCircle aria-hidden />
									{feature}
								</li>
							))}
						</ul>
						{hasMore && (
							<button
								type="button"
								className="pricing-more"
								onClick={() =>
									setExpanded((prev) => ({
										...prev,
										[index]: !isExpanded
									}))
								}
							>
								{isExpanded
									? 'Ver menos'
									: `Ver mais (${
											features.length - collapseAfter
									  })`}
							</button>
						)}
						{period.ctaUrl ? (
							<a
								className="pricing-cta"
								href={period.ctaUrl}
								style={{
									background: column.highlight
										? props.accentColor || undefined
										: undefined
								}}
							>
								{column.ctaLabel || 'Assinar'}
							</a>
						) : (
							isPreviewMode() && (
								<span
									className="pricing-cta pricing-cta--placeholder"
									title="Configure o link do plano no painel"
								>
									{column.ctaLabel || 'Assinar'}
								</span>
							)
						)}
					</div>
				);
			})}
		</div>
		</div>
	);
};

const BlockRenderer = ({
	block,
	checkoutId
}: {
	block: ICheckoutBlock;
	checkoutId?: number;
}) => {
	const { type, props } = block;

	switch (type) {
		case 'banner':
		case 'image':
			if (!props.imageUrl) {
				// No modo de edição, o bloco recém-criado precisa aparecer
				// (e ser clicável) antes de ter imagem.
				return isPreviewMode() ? (
					<div className="page-block page-block-placeholder">
						🖼️ {type === 'banner' ? 'Banner' : 'Imagem'} — envie a
						imagem no painel ao lado
					</div>
				) : null;
			}
			return (
				<img
					className={`page-block page-block-${type} ${
						type === 'image' && props.width === 'half'
							? 'page-block-image-half'
							: ''
					}`}
					src={props.imageUrl}
					alt={props.alt || ''}
				/>
			);

		case 'title':
			return (
				<h2
					className={`page-block page-block-title page-block-size-${
						props.size || 'md'
					}`}
					style={{
						textAlign: props.align || 'center',
						color: props.color || undefined,
						...(props.bgColor
							? {
									background: props.bgColor,
									padding: '14px 18px',
									borderRadius: 12
							  }
							: undefined)
					}}
				>
					{props.text}
				</h2>
			);

		case 'text':
			return (
				<p
					className={`page-block page-block-text page-block-size-${
						props.size || 'md'
					}`}
					style={{
						textAlign: props.align || 'left',
						color: props.color || undefined,
						...(props.bgColor
							? {
									background: props.bgColor,
									padding: '14px 18px',
									borderRadius: 12
							  }
							: undefined)
					}}
				>
					{props.text}
				</p>
			);

		case 'testimonials':
			return (
				<div className="page-block page-block-testimonials">
					{(props.items ?? []).map(
						(
							item: {
								name?: string;
								text?: string;
								avatarUrl?: string;
							},
							index: number
						) => (
							<div
								className="page-block-testimonial"
								key={index}
								style={{
									background: props.bgColor || undefined
								}}
							>
								{item.avatarUrl ? (
									<img
										className="page-block-testimonial-avatar"
										src={item.avatarUrl}
										alt={item.name || ''}
									/>
								) : (
									<FaQuoteLeft
										aria-hidden
										style={{
											color:
												props.accentColor || undefined
										}}
									/>
								)}
								<div>
									<p>{item.text}</p>
									<span>{item.name}</span>
								</div>
							</div>
						)
					)}
				</div>
			);

		case 'faq':
			// Accordion nativo (details/summary): expande/encolhe sem JS,
			// primeira pergunta aberta.
			return (
				<div className="page-block page-block-faq">
					{(props.items ?? []).map(
						(item: { q?: string; a?: string }, index: number) => (
							<details
								className="page-block-faq-item"
								key={index}
								open={index === 0 || undefined}
								style={{
									background: props.bgColor || undefined
								}}
							>
								<summary>
									<FaQuestionCircle
										aria-hidden
										style={{
											color:
												props.accentColor || undefined
										}}
									/>
									{item.q}
									<FaChevronDown
										aria-hidden
										className="page-block-faq-chevron"
									/>
								</summary>
								<span>{item.a}</span>
							</details>
						)
					)}
				</div>
			);

		case 'countdown':
			return <CountdownBlock block={block} checkoutId={checkoutId} />;

		case 'salesNotification':
			return <SalesNotificationBlock block={block} />;

		case 'pricingTable':
			return <PricingTableBlock block={block} />;

		case 'badges': {
			// Selos de confiança: segurança + formas de pagamento aceitas.
			const showCards = props.showCards !== false;
			const showPix = props.showPix !== false;
			const showBoleto = props.showBoleto !== false;
			return (
				<div className="page-block page-block-badges">
					<span className="badges-security">
						<FaLock aria-hidden />
						{props.securityText || 'Compra 100% segura'}
					</span>
					{(showCards || showPix || showBoleto) && (
						<span className="badges-methods">
							{showCards && (
								<>
									<FaCcVisa title="Visa" aria-label="Visa" />
									<FaCcMastercard
										title="Mastercard"
										aria-label="Mastercard"
									/>
									<FaCcAmex
										title="American Express"
										aria-label="American Express"
									/>
								</>
							)}
							{showPix && (
								<span
									className="badges-pix"
									title="Pix"
									aria-label="Pix"
								>
									<PixIcon />
								</span>
							)}
							{showBoleto && (
								<FaBarcode
									title="Boleto"
									aria-label="Boleto"
								/>
							)}
						</span>
					)}
					{props.extraText && (
						<span className="badges-extra">{props.extraText}</span>
					)}
				</div>
			);
		}

		case 'guarantee': {
			// Cor do selo dita borda/fundo (alpha em hex) e ícones.
			const accent: string | undefined = props.accentColor || undefined;
			// 'subtle' vira uma linha discreta colada ao botão (substitui o
			// antigo selo "Ambiente seguro" sem competir com a compra).
			if (props.variant === 'subtle') {
				return (
					<div
						className="page-block page-block-guarantee-subtle"
						style={{ color: accent }}
					>
						<FaShieldAlt aria-hidden />
						<span>
							{props.title ||
								`Garantia de ${props.days ?? 7} dias`}
							{' · '}
							{props.text || 'Compra segura'}
						</span>
					</div>
				);
			}
			return (
				<div
					className={`page-block page-block-guarantee ${
						props.variant === 'banner'
							? 'page-block-guarantee--banner'
							: ''
					}`}
					style={
						accent
							? {
									borderColor: `${accent}59`,
									background: `${accent}14`
							  }
							: undefined
					}
				>
					<FaShieldAlt aria-hidden style={{ color: accent }} />
					<div>
						<p>
							{props.title ||
								`Garantia de ${props.days ?? 7} dias`}
						</p>
						{props.text && <span>{props.text}</span>}
						<span
							className="page-block-guarantee-safe"
							style={{ color: accent }}
						>
							<FaCheckCircle aria-hidden /> Compra segura ·
							ambiente criptografado
						</span>
					</div>
				</div>
			);
		}

		case 'video': {
			const embed = props.url ? toEmbedUrl(props.url) : null;
			if (!embed) {
				return isPreviewMode() ? (
					<div className="page-block page-block-placeholder">
						▶️ Vídeo — cole a URL do YouTube/Vimeo no painel ao lado
					</div>
				) : null;
			}
			return (
				<div className="page-block page-block-video">
					<iframe
						src={embed}
						title="Vídeo"
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					/>
				</div>
			);
		}

		case 'columns': {
			const count = props.count === 3 ? 3 : 2;
			const children: ICheckoutBlock[][] = props.children ?? [];
			const widths: number[] =
				Array.isArray(props.widths) && props.widths.length === count
					? props.widths
					: Array.from({ length: count }).map(() => 100 / count);
			return (
				<div className="page-block page-block-columns">
					{Array.from({ length: count }).map((_, column) => (
						<div
							key={column}
							className="page-block-column"
							style={{
								['--col-width' as string]: `${widths[column]}%`
							}}
						>
							{(children[column] ?? []).map((child) => (
								<BlockRenderer
									key={child.id}
									block={child}
									checkoutId={checkoutId}
								/>
							))}
						</div>
					))}
				</div>
			);
		}

		case 'orderBump':
			return <OrderBumpBlock block={block} />;

		default:
			return null;
	}
};

/** Blocos acima do grid form+sidebar. */
export const PageBlocks = ({
	page,
	checkoutId
}: {
	page?: ICheckoutPage;
	checkoutId?: number;
}) => {
	if (!page?.blocks?.length) {
		// No modo de edição o container precisa existir mesmo vazio — é nele
		// que o builder desenha a zona de solta do topo da página.
		return isPreviewMode() ? (
			<div className="page-blocks" style={{ minHeight: 48 }} />
		) : null;
	}
	return (
		<div className="page-blocks">
			{page.blocks.map((block) => (
				<div key={block.id} data-block-id={block.id}>
					<BlockRenderer block={block} checkoutId={checkoutId} />
				</div>
			))}
		</div>
	);
};

/** Widgets da coluna lateral — abaixo do resumo da compra. */
export const PageAsideBlocks = ({
	page,
	checkoutId
}: {
	page?: ICheckoutPage;
	checkoutId?: number;
}) => {
	if (!page?.aside?.length) return null;
	return (
		<div className="page-blocks page-blocks-aside">
			{page.aside.map((block) => (
				<div key={block.id} data-block-id={block.id}>
					<BlockRenderer block={block} checkoutId={checkoutId} />
				</div>
			))}
		</div>
	);
};

/** Blocos do rodapé — abaixo do grid form+resumo (FAQ como continuidade). */
export const PageFooterBlocks = ({
	page,
	checkoutId
}: {
	page?: ICheckoutPage;
	checkoutId?: number;
}) => {
	if (!page?.footer?.length) return null;
	return (
		<div className="page-blocks page-blocks-footer">
			{page.footer.map((block) => (
				<div key={block.id} data-block-id={block.id}>
					<BlockRenderer block={block} checkoutId={checkoutId} />
				</div>
			))}
		</div>
	);
};

/**
 * Widgets ancorados no botão de compra (via CheckoutPageSlotsContext, para o
 * Form não precisar receber a página inteira). 'aboveButton' = order
 * bump/urgência; 'belowButton' = garantia/selos.
 */
export const PageSlotBlocks = ({
	slot,
	checkoutId
}: {
	slot: 'aboveButton' | 'belowButton';
	checkoutId?: number;
}) => {
	const slots = useContext(CheckoutPageSlotsContext);
	const blocks = slots?.[slot];
	if (!blocks?.length) return null;
	return (
		<div
			className={`page-blocks page-blocks-slot page-blocks-slot-${slot}`}
		>
			{blocks.map((block) => (
				<div key={block.id} data-block-id={block.id}>
					<BlockRenderer block={block} checkoutId={checkoutId} />
				</div>
			))}
		</div>
	);
};
